/**
 * buildserver / transfer
 *
 * Copyright © 2013-2022 Panascais ehf. and / or subsidiaries. All rights reserved.
 *
 * All information contained herein is, and remains the property
 * of Panascais ehf. and its suppliers, if any.
 * The intellectual and technical concepts contained herein are
 * proprietary to Panascais ehf. and its suppliers and may
 * be covered by European, U.S. and foreign patents, patents in process,
 * and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Panascais ehf.
 *
 * License:
 *
 * You are not allowed to copy, modify, merge, publish, distribute,
 * sublicense, and / or sell copies of the software.
 *
 * The above copyright notice and this permission notice shall be
 * included in all copies or substantial portions of the software.
 */

/* eslint-disable unicorn/consistent-function-scoping */
/* eslint-disable unicorn/prefer-dom-node-dataset */
/* eslint-disable unicorn/no-array-for-each */

/**
 * npm dependencies
 */
import axios, { AxiosProgressEvent } from 'axios';
import * as Clipboard from 'clipboard';
import { LowSync } from 'lowdb/lib/LowSync';
import { LocalStorage } from 'lowdb/lib/adapters/LocalStorage';
import { throttle } from 'throttle-debounce';
import { render, cancel } from 'timeago.js';
import { addHours, addMinutes, isAfter, isBefore } from 'date-fns';

/**
 * project dependencies
 */
import { Status } from '@internal/constants/http';
import { isNil, isNotNil } from '@internal/util/is';
import { formatBytes } from '@internal/util/bytes/format';
import { timeout } from '@internal/util/timeout';
import { second } from '@internal/constants/time';
import { megabyte } from '@internal/constants/bytes/number/metric';

type ToastTypes = 'success' | 'failed' | 'loading';

interface UploadResponseData {
    filename: string;
    id: string;
    extension: string;
}

interface DatabaseClipboardTransferModel {
    id: string;
    name: string;
    extension: string;
    filename: string;
    size: number;
    date: number;
}

interface DatabaseModel {
    clipboard: DatabaseClipboardTransferModel[];
}

type Database = LowSync<DatabaseModel>;

class Toast {
    private TOAST_ANIMATION_TIME = second / 1.25;

    private fadeOutTimer: { [key in ToastTypes]?: NodeJS.Timer } = {};

    private throttledShow = throttle(
        this.TOAST_ANIMATION_TIME + 5,
        (type: ToastTypes, message: string, timer?: number | false) => {
            const toast = document.querySelector(`.Toast.${type}`);

            if (!toast) {
                return;
            }

            const content = toast.querySelector('.Toast-content');

            if (!content) {
                return;
            }

            content.innerHTML = message;

            toast.classList.remove('Toast--animateOut');
            toast.classList.remove('d-none');

            if (timer !== false) {
                this.fadeOutTimer[type] = setTimeout(() => {
                    toast.classList.add('Toast--animateOut');
                }, timer ?? this.TOAST_ANIMATION_TIME);
            }
        },
        { noLeading: false, noTrailing: false }
    );

    private constructor() {}

    public static of() {
        return new Toast();
    }

    public show = (type: ToastTypes, message: string, timer?: number | false) => {
        this.clear(type);
        this.throttledShow(type, message, timer);
    };

    public hide = (type: ToastTypes) => {
        const toast = document.querySelector(`.Toast.${type}`);

        if (!toast) {
            return;
        }

        this.clear(type);

        toast.classList.add('Toast--animateOut');
    };

    private clear = (type: ToastTypes) => {
        if (this.fadeOutTimer[type]) {
            clearTimeout(this.fadeOutTimer[type]);
            delete this.fadeOutTimer[type];
        }
    };
}

class Copy {
    private element: HTMLElement;
    private toast: Toast;
    private text?: string;
    private timer?: number;
    private successMessage: string;
    private errorMessage: string;
    private callback?: (type: 'success' | 'error') => void;

    public constructor({
        element,
        toast,
        text,
        callback,
        timer,
        successMessage = 'Copied!',
        errorMessage = 'Failed to copy!',
    }: {
        element: HTMLElement;
        toast: Toast;
        text?: string;
        timer?: number;
        successMessage?: string;
        errorMessage?: string;
        callback?: (type: 'success' | 'error') => void;
    }) {
        if (isNil(element)) {
            throw new Error('missing element!');
        }
        this.element = element;
        this.toast = toast;
        this.text = text;
        this.callback = callback;
        this.timer = timer;
        this.successMessage = successMessage;
        this.errorMessage = errorMessage;

        this.start();
    }

    private start() {
        const content = this.element.textContent;

        if (isNil(content)) {
            return;
        }

        if (content.length > 0) {
            new Clipboard(this.element, {
                text: entry => {
                    return this.text ?? entry.textContent ?? '';
                },
            })
                .on('success', () => {
                    this.callback?.('success');
                    this.toast.show('success', this.successMessage, this.timer);
                })
                .on('error', () => {
                    this.callback?.('error');
                    this.toast.show('failed', this.errorMessage, this.timer);
                });
        }
    }
}

class CopyManager {
    private elements: HTMLElement[];

    private toast: Toast;

    private constructor({ selector, toast }: { selector: string; toast: Toast }) {
        const elements = document.querySelectorAll<HTMLElement>(selector);

        if (isNil(elements) || elements.length === 0) {
            throw new Error('no elements!');
        }

        this.elements = Array.from(elements);
        this.toast = toast;

        this.start();
    }

    public static of({ selector, toast }: { selector: string; toast: Toast }) {
        return new CopyManager({ selector, toast });
    }

    private start() {
        for (const element of this.elements) {
            new Copy({ element, toast: this.toast });
        }
    }
}

type UploadState = 'select' | 'drag' | 'upload';

class API {
    public static async upload({
        file,
        extension,
        csrf,
        handleProgress,
    }: {
        file: File;
        extension: string;
        csrf: string;
        handleProgress?: (progress: AxiosProgressEvent) => void;
    }) {
        const result = await axios.post<{
            data: UploadResponseData & { etag: string };
        }>('/api/clipboard/upload', file, {
            headers: {
                'Content-Type': 'application/octet-stream',
                'X-CSRF-Token': csrf,
            },
            onUploadProgress: handleProgress,
            params: {
                extension,
            },
            responseType: 'json',
        });

        const {
            data: { data },
            status,
        } = result;

        return { data, status };
    }

    public static async delete({ uuid, extension }: { uuid: string; extension: string }) {
        const result = await axios.delete<{
            data: { success: boolean };
        }>(`/api/clipboard/delete/${uuid}.${extension}`, {
            responseType: 'json',
        });

        const {
            data: { data },
            status,
        } = result;

        if (status !== Status.ACCEPTED) {
            return false;
        }

        return data.success;
    }
}

class Upload {
    public UPLOAD_AMOUNT = 0;

    public state: UploadState = 'select';

    private FILE_TYPES = ['schem', 'schematic', 'zip', 'nbt'];
    private FILE_LIST_LIMIT = 15;
    private MAX_FILE_SIZE_IN_MEGABYTES = 15;
    private MAX_FILE_SIZE = megabyte * this.MAX_FILE_SIZE_IN_MEGABYTES;

    private database: Database;
    private toast: Toast;
    private mainBox: HTMLElement;
    private uploadButton: HTMLElement;
    private filesBox: HTMLDivElement;
    private filesList: HTMLDivElement;
    private progress: HTMLSpanElement;
    private progressBar: HTMLSpanElement;
    private removeDragTimer?: NodeJS.Timer;

    private constructor(options: { toast: Toast; database: Database }) {
        this.toast = options.toast;
        this.database = options.database;

        const mainBox = document.querySelector<HTMLDivElement>('.Box.upload-box');

        if (isNil(mainBox)) {
            throw new Error("element '.Box.upload-box' missing!");
        }

        const uploadButton = document.querySelector<HTMLDivElement>('.Box-body button.upload-button');

        if (isNil(uploadButton)) {
            throw new Error("element '.Box-body button.upload-button' missing!");
        }

        const filesBox = document.querySelector<HTMLDivElement>('.Box.files-box');

        if (isNil(filesBox)) {
            throw new Error("element '.Box.files-box' missing!");
        }

        const filesList = filesBox.querySelector<HTMLDivElement>('.Box-row.list');

        if (isNil(filesList)) {
            throw new Error("element '.Box-row.list' missing!");
        }

        const progress = document.querySelector<HTMLSpanElement>('.Progress.upload-progress');

        if (isNil(progress)) {
            throw new Error("element '.Progress.upload-progress' missing!");
        }

        const progressBar = progress.querySelector<HTMLSpanElement>('.Progress-item');

        if (isNil(progressBar)) {
            throw new Error("element '.Progress-item' missing!");
        }

        this.mainBox = mainBox;
        this.uploadButton = uploadButton;
        this.filesBox = filesBox;
        this.filesList = filesList;
        this.progress = progress;
        this.progressBar = progressBar;

        this.start();
    }

    public static of(options: { toast: Toast; database: Database }) {
        return new Upload(options);
    }

    public async deleteFile({
        uuid,
        extension,
        callback,
    }: {
        uuid: string;
        extension: string;
        callback?: { success?: () => void | Promise<void>; failed?: () => void | Promise<void> };
    }) {
        const remove = () => {
            this.database.data ??= { clipboard: [] };

            this.database.data.clipboard = this.database.data.clipboard.filter(entry => {
                if (entry.id === uuid && entry.extension === extension) {
                    return false;
                }

                return true;
            });
            this.updateFileList();
            this.database.write();
        };

        API.delete({ uuid, extension })
            .then(result => {
                if (result) {
                    remove();
                    void callback?.success?.();
                } else {
                    remove();
                    void callback?.failed?.();
                }
            })
            .catch(() => {
                remove();
                void callback?.failed?.();
            });
    }

    private start() {
        window.addEventListener('dragover', this.cancelEvent);
        window.addEventListener('drop', this.cancelEvent);

        this.database.read();

        this.database.data ??= { clipboard: [] };
        this.database.data.clipboard ??= [];

        this.database.data.clipboard = this.database.data.clipboard.filter(Boolean);

        this.database.write();

        this.updateFileList();

        this.startListeners();
    }

    private setState = (state: UploadState) => {
        this.state = state;

        this.mainBox.querySelectorAll('.Box-body > div').forEach(element => {
            element.classList.remove('active');
        });

        this.mainBox.querySelector(`.Box-body > div.${state}`)?.classList.add('active');
    };

    private startListeners() {
        this.mainBox.addEventListener('dragover', this.addDragoverListener);
        this.mainBox.addEventListener('dragenter', this.addDragoverListener);
        this.mainBox.addEventListener('dragend', this.removeDragoverListener);
        this.mainBox.addEventListener('dragleave', this.removeDragoverListener);
        this.mainBox.addEventListener('drop', this.uploadFileByDragListener);
        this.uploadButton.addEventListener('click', this.uploadFileByButtonListener);
    }

    private stopListeners() {
        this.mainBox.removeEventListener('dragover', this.addDragoverListener);
        this.mainBox.removeEventListener('dragenter', this.addDragoverListener);
        this.mainBox.removeEventListener('dragend', this.removeDragoverListener);
        this.mainBox.removeEventListener('dragleave', this.removeDragoverListener);
        this.mainBox.removeEventListener('drop', this.uploadFileByDragListener);
        this.uploadButton.removeEventListener('click', this.uploadFileByButtonListener);
    }

    private addDragoverListener = (event: Event) => {
        this.cancelEvent(event);
        clearTimeout(this.removeDragTimer);
        this.setState('drag');
    };

    private removeDragoverListener = (event: Event) => {
        this.cancelEvent(event);
        this.removeDragTimer = setTimeout(() => {
            if (this.state === 'drag') {
                this.setState('select');
            }
        }, 50);
    };

    private uploadFileByDragListener = (event: DragEvent) => {
        this.cancelEvent(event);

        clearTimeout(this.removeDragTimer);

        this.removeDragTimer = setTimeout(() => {
            if (this.state === 'drag') {
                this.setState('select');
            }
        }, 50);

        const files = event.dataTransfer?.files;

        if (!files || files.length === 0) {
            return;
        }

        this.stopListeners();

        void this.uploadFiles({ files });
    };

    private uploadFileByInputListener = (event: Event, element: HTMLInputElement, listener: (event: Event) => void) => {
        this.cancelEvent(event);

        clearTimeout(this.removeDragTimer);

        const { files } = element;

        void this.uploadFiles({ files }).then(() => {
            element.removeEventListener('change', listener);
            element.remove();
        });
    };

    private uploadFileByButtonListener = () => {
        const element = document.createElement('input');
        element.accept = this.FILE_TYPES.map(type => `.${type}`).join(',');
        element.classList.add('d-none');
        element.type = 'file';
        element.multiple = true;

        document.body.append(element);

        const eventListener = (event: Event) => {
            this.uploadFileByInputListener(event, element, eventListener);
        };

        element.addEventListener('change', eventListener);

        element.click();
    };

    private uploadFiles = async ({ files }: { files: Nullable<FileList> }) => {
        if (isNil(files) || files.length === 0) {
            return;
        }

        this.stopListeners();

        const list = Array.from(files);

        const amount = list.length;

        for (const [index, file] of list.entries()) {
            const isLast = index === files.length - 1;

            await this.uploadFile({ file, isLast, group: { index, amount } });

            if (isLast) {
                this.startListeners();
            }
        }
    };

    private uploadFile = async ({
        file,
        isLast,
        group,
    }: {
        file: File;
        isLast: boolean;
        group?: { index: number; amount: number };
    }) => {
        const check = this.checkFile(file);

        if (!check.isValid) {
            this.endUploadHandling({ state: 'failed', isLast, message: check.message });
            return;
        }

        const { extension } = check;

        const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('data-value');

        if (isNil(csrf) || csrf === '') {
            this.endUploadHandling({ state: 'failed', message: 'Missing CSRF token!', isLast });
            return;
        }

        this.setState('upload');

        this.startUploadHandling(file);

        try {
            const { data, status } = await API.upload({
                file,
                extension,
                csrf,
                handleProgress: progress => {
                    const percentage = this.calculatePercentage(file.size, progress.loaded, group);
                    this.updateProgress(percentage);
                },
            });

            if (status >= Status.BAD_REQUEST) {
                this.endUploadHandling({ state: 'failed', isLast });
            }

            this.UPLOAD_AMOUNT += 1;

            const name = file.name.split('.').slice(0, -1).join('.');

            await timeout(second / 10);

            this.endUploadHandling({
                state: 'success',
                data,
                status,
                name,
                size: file.size,
                filename: file.name,
                isLast,
            });
        } catch (exception) {
            console.error(exception);

            this.endUploadHandling({ state: 'failed', isLast });
        }
    };

    private updateFileList() {
        this.database.write();
        this.database.read();

        this.database.data ??= { clipboard: [] };

        const models = this.database.data.clipboard;

        cancel();

        const now = new Date();

        const results = models
            .sort((a, b) => {
                if (a.date < b.date) {
                    return 1;
                }

                if (a.date > b.date) {
                    return -1;
                }

                return 0;
            })
            .filter(model => {
                if (isAfter(now, addHours(model.date, 23))) {
                    return false;
                }

                return true;
            })
            .slice(0, this.FILE_LIST_LIMIT)
            .map((model, index) => {
                return this.generateFileListEntry({ model, index });
            });

        if (results.length === 0) {
            this.filesList.innerHTML = '';
            this.filesBox.classList.add('d-none');
            return;
        }

        this.filesList.innerHTML = '';

        for (const result of results) {
            this.filesList.append(...result);
        }

        this.filesBox.classList.remove('d-none');
    }

    private generateFileListEntryIcon = ({ isNew, expiringSoon }: { isNew: boolean; expiringSoon: boolean }) => {
        if (expiringSoon) {
            return 'red';
        }

        if (isNew) {
            return 'green';
        }

        return false;
    };

    private generateFileListEntry = ({ model, index }: { model: DatabaseClipboardTransferModel; index: number }) => {
        const { id, extension, name, filename, date, size } = model;

        const url = new URL(document.location.protocol + document.location.host);

        url.pathname = '/clipboard';
        url.port = document.location.port.replace(':', '');
        url.search = new URLSearchParams({ key: id, type: extension }).toString();

        const now = new Date();

        const isFirst = index === 0;
        const isNew = isFirst || isBefore(now, addMinutes(date, 5));
        const expiringSoon = isAfter(now, addHours(date, 20));

        const iconName = this.generateFileListEntryIcon({ isNew, expiringSoon });

        const file = {
            name,
            extension,
            size: formatBytes(size, { maximumFractionDigits: 1 }),
        };

        const createElement = <T extends HTMLElement = HTMLElement>(value: string): T => {
            const element = document.createElement('div');
            element.innerHTML = value;

            return element.firstElementChild as T;
        };

        const divider = isFirst ? undefined : createElement<HTMLDivElement>('<div class="divider"></span>');

        const statusIconBox = iconName ? `<div class="status-dot status-dot--${iconName}"></div>` : '';

        const fileExtensionBox = createElement<HTMLSpanElement>(
            `<span class="Label Label--secondary rounded-2 file-extension">${file.extension}</span>`
        );

        const fileNameBox = createElement<HTMLSpanElement>(
            `<span class="filename Truncate"><b class="Font-monospace Truncate-text">${file.name}</b></span>:`
        );

        const minecraftCommandBox = createElement<HTMLDivElement>(
            `<code class="Font-monospace minecraft-command copy">//schematic load ${extension} url:${id}</code>`
        );

        const fileSizeBox = createElement<HTMLSpanElement>(`<span class="filesize"><b>${file.size}</b></span>:`);

        const timestampWrapper = createElement<HTMLDivElement>(`<div class="status">${statusIconBox}</div>`);
        const timestampBox = createElement<HTMLSpanElement>(
            `<span class="date" datetime="${new Date(date).toISOString()}"></span>`
        );
        timestampWrapper.append(timestampBox);

        const copyButton = createElement<HTMLButtonElement>(
            `<button class="btn btn-sm btn-primary color-bg-accent-emphasis copy ml-2" type="button" data-text="//schematic load ${extension} url:${id}">
                <svg class='octicon copy' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16">
                    <path fill-rule="evenodd" d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 010 1.5h-1.5a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-1.5a.75.75 0 011.5 0v1.5A1.75 1.75 0 019.25 16h-7.5A1.75 1.75 0 010 14.25v-7.5z"></path><path fill-rule="evenodd" d="M5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0114.25 11h-7.5A1.75 1.75 0 015 9.25v-7.5zm1.75-.25a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-7.5a.25.25 0 00-.25-.25h-7.5z"></path>
                </svg>
                <span>Copy</span>
            </button>`
        );

        const deleteButton = createElement<HTMLButtonElement>(
            `<button class="btn btn-sm btn-danger delete ml-2" type="button" data-uuid="${id}" data-extension="${extension}">
                <svg class="octicon delete" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16">
                    <path fill-rule="evenodd" d="M6.5 1.75a.25.25 0 01.25-.25h2.5a.25.25 0 01.25.25V3h-3V1.75zm4.5 0V3h2.25a.75.75 0 010 1.5H2.75a.75.75 0 010-1.5H5V1.75C5 .784 5.784 0 6.75 0h2.5C10.216 0 11 .784 11 1.75zM4.496 6.675a.75.75 0 10-1.492.15l.66 6.6A1.75 1.75 0 005.405 15h5.19c.9 0 1.652-.681 1.741-1.576l.66-6.6a.75.75 0 00-1.492-.149l-.66 6.6a.25.25 0 01-.249.225h-5.19a.25.25 0 01-.249-.225l-.66-6.6z"></path>
                </svg>
                <span class="text">Delete</span>
            </button>`
        );

        const shareButton = createElement<HTMLButtonElement>(
            `<button class="btn btn-sm" type="button" data-link="${url.toString()}">
                <svg class='octicon share' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16">
                    <path fill='currentColor' fill-rule="evenodd" d="M10.604 1h4.146a.25.25 0 01.25.25v4.146a.25.25 0 01-.427.177L13.03 4.03 9.28 7.78a.75.75 0 01-1.06-1.06l3.75-3.75-1.543-1.543A.25.25 0 0110.604 1zM3.75 2A1.75 1.75 0 002 3.75v8.5c0 .966.784 1.75 1.75 1.75h8.5A1.75 1.75 0 0014 12.25v-3.5a.75.75 0 00-1.5 0v3.5a.25.25 0 01-.25.25h-8.5a.25.25 0 01-.25-.25v-8.5a.25.25 0 01.25-.25h3.5a.75.75 0 000-1.5h-3.5z"></path>
                </svg>
                <span>Share</span>
            </button>`
        );

        const downloadButton = createElement<HTMLAnchorElement>(
            `<a class="btn btn-sm mx-2" type="button" download="${filename}" href="/api/clipboard/download/${id}.${extension}">
                <svg class='octicon download' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' width='16' height='16'>
                    <path fill='currentColor' fill-rule='evenodd' d='M13.03 8.22a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06 0L3.47 9.28a.75.75 0 011.06-1.06l2.97 2.97V3.75a.75.75 0 011.5 0v7.44l2.97-2.97a.75.75 0 011.06 0z'></path>
                </svg>
                <span>Download</span>
            </a>`
        );

        const buttons = createElement<HTMLDivElement>('<div class="buttons d-flex"></div>');
        buttons.append(shareButton, downloadButton, deleteButton, copyButton);

        const fileData = createElement<HTMLDivElement>('<div class="filedata"></div>');
        fileData.append(fileNameBox, timestampWrapper, fileSizeBox, fileExtensionBox);

        const elements: HTMLDivElement[] = [divider, fileData, minecraftCommandBox, buttons].filter(
            (element): element is HTMLDivElement => {
                return isNotNil(element);
            }
        );

        deleteButton.addEventListener('click', () => {
            const data = deleteButton.dataset as { uuid: string; extension: string };

            const icon = deleteButton.querySelector('svg.octicon');
            const content = deleteButton.querySelector('span.text');

            if (isNil(data.uuid) || isNil(data.extension)) {
                return;
            }

            if (isNotNil(icon) && isNotNil(content)) {
                content.innerHTML = '';
                icon.classList.add('d-none');
                content.classList.add('AnimatedEllipsis');
            }

            void this.deleteFile({
                ...data,
                callback: {
                    success: () => {
                        this.toast.show(
                            'success',
                            `Successfully deleted <span class="filename Truncate"><b class="Font-monospace Truncate-text">${file.name}</b></span>!`
                        );
                    },
                    failed: () => {
                        this.toast.show(
                            'failed',
                            `Failed to delete <span class="filename Truncate"><b class="Font-monospace Truncate-text">${file.name}</b></span>!`
                        );
                    },
                },
            });
        });

        new Copy({
            element: minecraftCommandBox,
            toast: this.toast,
            successMessage: `Copied command for <span class="filename Truncate"><b class="Font-monospace Truncate-text">${file.name}</b></span>`,
            timer: second * 0.75,
        });

        if (shareButton.dataset.link?.length) {
            new Copy({
                element: shareButton,
                toast: this.toast,
                text: shareButton.dataset.link,
                successMessage: `Copied link for <span class="filename Truncate"><b class="Font-monospace Truncate-text">${file.name}</b></span>`,
                timer: second * 0.75,
            });
        }

        if (copyButton.dataset.text?.length) {
            new Copy({
                element: copyButton,
                toast: this.toast,
                text: copyButton.dataset.text,
                successMessage: `Copied command for <span class="filename Truncate"><b class="Font-monospace Truncate-text">${file.name}</b></span>`,
                timer: second * 0.75,
                callback: type => {
                    const text = copyButton.querySelector('span');

                    if (type === 'success' && text) {
                        text.textContent = 'Copied!';

                        setTimeout(() => {
                            text.textContent = 'Copy';
                        }, second);
                    }
                },
            });
        }

        render(timestampBox);

        return elements;
    };

    private checkFile = (
        file: File
    ): { isValid: false; message: string; extension: string } | { isValid: true; extension: string } => {
        const extension = file.name.split('.').pop();

        if (isNil(extension)) {
            return {
                isValid: false,
                message: `<b class="Font-monospace">${file.name}:</b> Unable to resolve file extension!`,
                extension: 'unknown',
            };
        }

        if (!this.FILE_TYPES.includes(extension)) {
            return {
                isValid: false,
                message: `<b class="Font-monospace">${file.name}:</b> Invalid file extension!`,
                extension,
            };
        }

        if (file.size > this.MAX_FILE_SIZE) {
            return { isValid: false, message: `<b class="Font-monospace">${file.name}:</b> File too big!`, extension };
        }

        return { isValid: true, extension };
    };

    private calculatePercentage = (size: number, loaded: number, group?: { index: number; amount: number }) => {
        let percentage = Math.floor((loaded / size) * 100);

        percentage = Math.max(percentage, 0);
        percentage = Math.min(percentage, 100);

        if (group) {
            const part = 100 / group.amount;

            return group.index * part + (part / 100) * percentage;
        }

        return percentage;
    };

    private updateProgress = (percentage: number) => {
        if (isNil(this.progress) || isNil(this.progressBar)) {
            return;
        }

        this.progressBar.style.width = `${Math.floor(percentage)}%`;
    };

    private startUploadHandling = (file: File) => {
        if (isNil(this.progress) || isNil(this.progressBar)) {
            return;
        }

        if (file.size > megabyte * 5) {
            this.toast.show('loading', 'Uploading your schematic...', false);
        }

        this.progressBar.style.width = '0%';

        this.progress.classList.remove('d-none', 'anim-fade-out');
    };

    private endUploadHandling = (
        options:
            | { state: 'failed'; isLast: boolean; message?: string }
            | {
                  state: 'success';
                  data: UploadResponseData;
                  name: string;
                  size: number;
                  filename: string;
                  status: number;
                  isLast: boolean;
                  message?: string;
              }
    ) => {
        if (options.isLast) {
            this.toast.hide('loading');

            this.progress.classList.add('anim-fade-out');

            this.progressBar.style.width = '0%';

            this.setState('select');
        }

        switch (options.state) {
            case 'success': {
                const { filename, data, state, name, size, message } = options;
                const { id, extension } = data;

                const model: DatabaseClipboardTransferModel = {
                    id,
                    name,
                    extension,
                    size,
                    filename,
                    date: Date.now(),
                };

                this.database.data ??= { clipboard: [] };

                this.database.data.clipboard.push(model);

                this.database.write();

                this.updateFileList();

                this.toast.show(state, message ?? 'Uploaded your schematic.', second * 3);
                break;
            }
            case 'failed': {
                const { state, message } = options;
                this.toast.show(state, message ?? 'Failed to upload your schematic!', second * 3);
                break;
            }
        }
    };

    private cancelEvent = (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
    };
}

class Delete {
    private toast: Toast;
    private upload: Upload;

    private flash: HTMLDivElement;
    private button: HTMLButtonElement;

    private constructor({
        toast,
        flash,
        button,
        upload,
    }: {
        toast: Toast;
        flash: HTMLDivElement;
        button: HTMLButtonElement;
        upload: Upload;
    }) {
        this.toast = toast;
        this.flash = flash;
        this.button = button;
        this.upload = upload;

        this.start();
    }

    public static of({ toast, upload }: { toast: Toast; upload: Upload }) {
        const flash = document.querySelector<HTMLDivElement>('.flash.flash-success');

        if (isNil(flash)) {
            return;
        }

        const button = flash.querySelector<HTMLButtonElement>('button.delete');

        if (isNil(button)) {
            return;
        }

        return new Delete({ toast, flash, button, upload });
    }

    private start() {
        this.button.addEventListener('click', () => {
            const { uuid, extension } = this.button.dataset;

            const icon = this.button.querySelector('svg.octicon');
            const content = this.button.querySelector('span.text');

            if (isNil(uuid) || isNil(extension)) {
                return;
            }

            if (isNotNil(icon) && isNotNil(content)) {
                content.innerHTML = '';
                icon.classList.add('d-none');
                content.classList.add('AnimatedEllipsis');
            }

            void this.upload.deleteFile({
                uuid,
                extension,
                callback: {
                    success: () => {
                        this.toast.show('success', 'Successfully deleted the file!');
                        this.flash.classList.add('fade-out');
                    },
                    failed: () => {
                        this.toast.show('failed', 'Failed to delete the file!');
                    },
                },
            });
        });
    }
}

window.addEventListener('load', () => {
    const database = new LowSync<DatabaseModel>(new LocalStorage('transfer-database'));

    const toast = Toast.of();

    const upload = Upload.of({ toast, database });

    CopyManager.of({ selector: 'code.minecraft-command.copy', toast });
    Delete.of({ toast, upload });
});
